import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { BotIcon, LayoutGridIcon, WrenchIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { launchAgentWithWidget } from '@/features/chats';
import { BrowseByCategorySection, FeaturedCardsRow, ToolsGrid } from '@/features/tools-hub';
import { useSpringboardTabs, useToolsCatalog } from '@/features/springboard';
import { useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { StaggerItem } from '@/shared/ui/animations';
import {
  ListPageLayout,
  PageHeader,
  PageHeaderIdentity,
  PageHeaderTitle,
} from '@/shared/ui/layout';
import { AboutPageDialog } from '@/shared/ui';
import { ROUTES } from '@/shared/config/routes';
import type { ToolCategoryId } from '@/shared/constants/category-colors';
import { useCurrentWorkspaceSlug } from '@/shared/hooks';
import { FLOW_DIGEST_AGENT_ID } from '@/shared/utils/flow-digest';

const CATEGORY_ROUTES: Record<ToolCategoryId, string> = {
  plan: ROUTES.TOOLS_PLAN,
  create: ROUTES.TOOLS_CREATE,
  scale: ROUTES.TOOLS_SCALE,
  deliver: ROUTES.TOOLS_DELIVER,
};

/**
 * Redesigned tools page.
 *
 * The header is the title and its About dialog, which is where the page
 * description now lives — the 56px header has no room for it. Below it the two
 * featured tools, then the whole catalog behind a search and two filters, then
 * the category doors for anyone who arrived without a tool in mind.
 *
 * Smart starts are gone from this page: the redesign puts the catalog where
 * they were, and they remain on each category page.
 */
export const ToolsHub = () => {
  const { t } = useTranslation('tools');

  const [isAboutOpen, setIsAboutOpen] = useState(false);

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const workspaceId = useCurrentWorkspaceId();
  const workspaceSlug = useCurrentWorkspaceSlug();
  const organizationId = getCurrentOrganizationId();
  const { tabs } = useSpringboardTabs();

  const { isAgentEnabled, isLoading: isFlowDigestLoading } = useAgentVisibility(
    [FLOW_DIGEST_AGENT_ID],
    { workspaceId, organizationId },
  );

  const { tools, isLoading: isCatalogLoading } = useToolsCatalog({
    showFlowDigest: isAgentEnabled(FLOW_DIGEST_AGENT_ID),
    workspaceSlug,
    onLaunchAgent: (agentId) => launchAgentWithWidget(navigate, agentId, { backTo: pathname }),
  });

  // Flow Digest is gated by this page's own visibility query, so the catalog
  // cannot know it is still resolving — without this the card pops in after the
  // skeletons have cleared.
  const isLoading = isCatalogLoading || isFlowDigestLoading;

  const categoryLabels = Object.fromEntries(
    tabs.map((tab) => [tab.id, { label: tab.label, subtitle: tab.subtitle }]),
  ) as Partial<Record<ToolCategoryId, { label: string; subtitle: string }>>;

  return (
    <div data-testid="dashboard-layout-padded" className="page-layout">
      <ListPageLayout>
        {/* Staggered entrance on the shared 50ms rhythm: header, then each
            block below it. */}
        <StaggerItem index={0}>
          <PageHeader>
            <PageHeaderIdentity>
              <PageHeaderTitle info={{ onClick: () => setIsAboutOpen(true) }}>
                {t('toolsHub.tools')}
              </PageHeaderTitle>
            </PageHeaderIdentity>
          </PageHeader>
        </StaggerItem>

        <div className="flex min-h-0 flex-1 flex-col gap-app-inset overflow-y-auto">
          <FeaturedCardsRow showCta />

          <StaggerItem index={3}>
            <ToolsGrid
              tools={tools}
              isLoading={isLoading}
              categoryLabels={Object.fromEntries(
                Object.entries(categoryLabels).map(([id, value]) => [id, value.label]),
              )}
            />
          </StaggerItem>

          <StaggerItem index={4}>
            <BrowseByCategorySection
              categoryLabels={categoryLabels}
              onCategoryClick={(category) => void navigate(CATEGORY_ROUTES[category])}
            />
          </StaggerItem>
        </div>
      </ListPageLayout>

      <AboutPageDialog
        open={isAboutOpen}
        onOpenChange={setIsAboutOpen}
        title={t('toolsHub.about.title')}
        description={t('toolsHub.about.description')}
        rows={[
          { icon: LayoutGridIcon, text: t('toolsHub.about.withoutLeavingTheConsole') },
          { icon: WrenchIcon, text: t('toolsHub.about.reachForTheStageYouAreIn') },
          { icon: BotIcon, text: t('toolsHub.about.putAgentsToWork') },
        ]}
      />
    </div>
  );
};
